import { useSelector } from 'react-redux'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from "react-router-dom"
import { useDispatch } from 'react-redux';
import { LOGOUT, LOAD_PACKAGELIST, LOAD_END, LOAD_DEFAULTS, LOAD_WORKERS, TABLE_OF_TABLES, LOAD_CLIENTS, LOAD_TRANSPORT_AREAS, LOAD_WORKER_TYPE, LOAD_PERSONAL_RIGHTS } from '../../../store/actions/actionsTypes'
import './second.scss'
import { ContextCefi } from '../../modules/Context/CefiNum'
import { loadDefaults } from '../../modules/requests/loadDefaults'
import styled from 'styled-components';
import LoaderSpinner from '../../modules/loadSpinner/LoaderSpinner';
import IfDisconnect from '../../generic/ifDisconnect/IfDisconnect';
import AppRouting from '../../Routs/AppRouting';
import { Sidebar, useSidebarCollapsed } from '../../../features/Sidebar';
import { Defaults } from '../../modules/API/Defaults/defaults';
import ColumModal from '../../generic/Layouts/ColumModal';
import PrinterSettingsModal from '../../generic/modals/PrinterSettingsModal';
import { SET_IS_OPEN } from '../../../store/reducers/printer';
import { BrodMigdalProvider } from '../../generic/modals/BrodMigdalModal/BrodMigdalProvider';
import { ErrorBoundary } from 'react-error-boundary';
import GeneralFallback from '../../generic/ErrorBoundary/GeneralFallback';
import { useAppVersionCheck } from '../../generic/hooks/useAppVersionCheck';
import AppVersionPrompt from '../../generic/cards/AppVersionPrompt/AppVersionPrompt';
import { AppModal } from '../../generic/ui/Modal';
import PatchNoteView from '../../generic/patch-note/PatchNoteView';

const MOBILE_QUERY = '(max-width: 769px)'

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined') return false
        return window.matchMedia(MOBILE_QUERY).matches
    })

    useEffect(() => {
        const mql = window.matchMedia(MOBILE_QUERY)
        const onChange = (e) => setIsMobile(e.matches)
        mql.addEventListener('change', onChange)
        return () => mql.removeEventListener('change', onChange)
    }, [])

    return isMobile
}

const MainSection = styled.div`
    font-family: ${({ theme }) => theme.fonts.sans};
    background: ${({ theme }) => theme.colors.bgLayout};
    color: ${({ theme }) => theme.colors.ink};
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
    /* While the on-screen keyboard is open keep the full height (see useKeyboardViewportLock). */
    html[data-vk-lock] & {
        height: var(--app-lock-height);
    }
    /* No direction here — each module manages its own RTL/LTR.
       Sidebar position (visual right) is handled via row-reverse below. */
`

const Layout = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    /* JSX order is Sidebar first, Content second. row-reverse puts Sidebar
       on the visual right while keeping the parent direction LTR so modules
       inside Content render with their own intended direction. */
    flex-direction: row-reverse;
    align-items: stretch;
    gap: 12px;
    padding: 12px;

    @media (max-width: 769px) {
        flex-direction: column;
        padding: 0;
        gap: 0;
    }
`

const Content = styled.section`
    position: relative;
    flex: 1;
    min-width: 0;
    /* Bounded by the viewport — scrolling lives inside individual modules. */
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media screen and (max-height: 500px) {
        overflow-y: auto;
    }
`

const BurgerBtn = styled.button`
    position: fixed;
    top: max(6px, env(safe-area-inset-top, 0px));
    inset-inline-end: 6px;
    width: 40px;
    height: 40px;
    border: 1px solid ${({ theme }) => theme.colors.line};
    background: ${({ theme }) => theme.colors.surface};
    border-radius: 10px;
    color: ${({ theme }) => theme.colors.ink};
    display: grid;
    place-items: center;
    cursor: pointer;
    z-index: 55;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
    transition:
        background ${({ theme }) => theme.motion.fast},
        border-color ${({ theme }) => theme.motion.fast};

    &:hover {
        background: ${({ theme }) => theme.colors.line2};
        border-color: ${({ theme }) => theme.colors.inputBorderHover};
    }

    svg { width: 18px; height: 18px; }
`

export const LoadingModal = styled.div`
    width: 100%;
    height: 100%;
    position: fixed;
    background: rgba(241, 242, 243, 0.9);
    z-index: 100;
    display: flex;
    justify-content: center;
    align-items: center;
`

function SecondScreen() {
    const modules = useSelector(module => module.mainReducer.modules);
    const [iOnline, setIsOnline] = useState(window?.navigator?.onLine);
    const [isFullFill, setIsFullFill] = useState(false);
    const [cefiNum, setCefiNum] = useState('')
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [subName, setSubName] = useState('')
    const dispatch = useDispatch()
    const location = useLocation();
    const [workersList, setWorkersList] = useState(null)
    const companies = useSelector(user => user.mainReducer.companies)
    const loadStatus = useSelector(load => load.mainReducer.loading);
    const idForWorker = useSelector(token => token?.mainReducer?.user?.code_internal);
    const getToken = useSelector(token => token.mainReducer.token);
    const getLogin = useSelector(token => token.mainReducer.login);
    const userRuls = useSelector(userStatus => userStatus.mainReducer.user !== null ? userStatus.mainReducer.user.rights : '');
    const controller = useRef(new AbortController());
    const printerState = useSelector(printer => printer.printerReducer)
    const isAllowToShowEdits = useMemo(() => {
        return userRuls?.includes('ADMIN') ||
            userRuls?.includes('OFFICE') ||
            userRuls?.includes('DEV') ||
            companies?.length > 1
    }, [userRuls, companies])

    const versionCheck = useAppVersionCheck()
    const [forceShowPrompt, setForceShowPrompt] = useState(false)
    const [isPatchOpen, setIsPatchOpen] = useState(false)
    const showVersionPrompt = (versionCheck.state === 'mismatch-prompt' || forceShowPrompt) && !!versionCheck.remoteVersion
    const hasNewVersion = (versionCheck.state === 'mismatch-prompt' || versionCheck.state === 'mismatch-skipped') && !!versionCheck.remoteVersion
    const canViewPatchNote = useMemo(() => (
        userRuls?.includes('ADMIN') || userRuls?.includes('OFFICE') || userRuls?.includes('DEV')
    ), [userRuls])
    const handleVersionSkip = () => {
        versionCheck.skip()
        setForceShowPrompt(false)
    }

    const isMobile = useIsMobile()
    const [collapsed, toggleCollapsed] = useSidebarCollapsed()

    const openDrawer = useCallback(() => setDrawerOpen(true), [])
    const closeDrawer = useCallback(() => setDrawerOpen(false), [])

    /* Close mobile drawer automatically on route change. */
    useEffect(() => {
        if (drawerOpen) setDrawerOpen(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])

    /* Close drawer if viewport switches to desktop while it's open. */
    useEffect(() => {
        if (!isMobile) setDrawerOpen(false)
    }, [isMobile])

    useMemo(() => {
        if (!workersList || !idForWorker) return
        dispatch({ type: LOAD_WORKER_TYPE, payload: workersList[+idForWorker]?.Role_108 })
    }, [workersList, idForWorker])

    useEffect(() => {
        dispatch({ type: LOAD_END })
    }, [])

    // listen when leave from settings fetch defaults
    let arr = []
    let temp = useRef(arr);

    useEffect(() => {
        const url = '/main/settings/account'
        if (location?.pathname === url) {
            temp.current.push(location?.pathname)
        } else {
            if (temp.current[0] === url) {
                nessesaryDataLoad()
            }
            temp.current = []
        }
    }, [location])

    const getPackeges = async () => {
        try {
            let res = await Defaults.getPackages()
            dispatch({ type: LOAD_PACKAGELIST, payload: res?.packages?.filter(item => item.Active) })
        } catch (err) {
            console.log(err.response.data)
            if (err.response?.data.message === 'jwt malformed' || err.response?.data.message === 'jwt expired') {
                dispatch({ type: LOGOUT, payload: false });
            }
        }
    }
    const getClients = async () => {
        try {
            let res = await Defaults.getCustomers()
            const formatedCustomers = res.customers.filter(item => item.Active)
            const reduced = formatedCustomers.reduce((acc, cur) => {
                acc[cur.CustomerID] = cur
                return acc
            }, {})
            dispatch({ type: LOAD_CLIENTS, payload: { list: formatedCustomers, hashTable: reduced } })
        } catch (err) {
            console.log(err.response)
        }
    }
    const getWorkers = async () => {
        try {
            let res = await Defaults.getWorkers()
            dispatch({ type: LOAD_WORKERS, payload: res.workers });
            let reducedArr = res.workers.reduce((acc, cur) => {
                acc[cur.WorkerId] = cur
                return acc
            }, {})
            setWorkersList(reducedArr);
        } catch (err) {
            console.log(err.response)
        }
    }
    const getRights = async () => {
        try {
            const res = await Defaults.getRights()
            dispatch({ type: LOAD_PERSONAL_RIGHTS, payload: res })
        } catch (err) {
            console.log(err.response)
        }
    }
    const getTransportAreaCodes = async () => {
        try {
            let res = await Defaults.getTransportAreas()
            const reducedDict = res.transportAreas.reduce((acc, cur) => {
                acc[cur.AreaID] = cur
                return acc
            }, {})
            dispatch({
                type: LOAD_TRANSPORT_AREAS,
                payload: { array: res.transportAreas, dict: reducedDict },
            })
        } catch (err) {
            console.log(err.response)
        }
    }

    useEffect(() => {
        nessesaryDataLoad()
        return () => {
            controller.current.abort("Component got unmounted");
        };
    }, [])

    const nessesaryDataLoad = async () => {
        setIsFullFill(false)
        try {
            const promisses = [
                getRights(),
                getTransportAreaCodes(),
                getClients(),
                getPackeges(),
                getWorkers(),
                loadDefaults(getToken, LOAD_DEFAULTS, TABLE_OF_TABLES, dispatch),
            ]
            await Promise.all(promisses)
            setIsFullFill(true)
        } catch (err) {
            // swallow — same behavior as legacy
        }
    }

    useEffect(() => {
        if (!userRuls) return setSubName('')
        if (userRuls.includes('CUSTOMER')) setSubName('לקוח')
        if (userRuls.includes('GROWER')) setSubName('מגדל')
        if (userRuls.includes('SELLER')) setSubName('משווק')
        if (userRuls.includes('ADMIN')) setSubName('מנהל')
    }, [userRuls])

    useEffect(() => {
        setIsOnline(window.navigator.onLine)
    }, [window?.navigator?.onLine])

    const checkOnline = () => {
        setIsOnline(window?.navigator?.onLine)
    }

    const handleControlPront = () => {
        dispatch({ type: SET_IS_OPEN, payload: false })
    }

    const loadScreenToggle = loadStatus ? 'flex' : 'none'
    const logoName = useSelector(logo => logo.mainReducer.login !== null ? logo.mainReducer.user : 'check connection')

    return isFullFill ? (
        <MainSection className='wrapper-menu'>
            {printerState?.isOpen && (
                <ColumModal
                    title='הגדרות מדפסת'
                    Children={PrinterSettingsModal}
                    controlFunc={handleControlPront}
                    childrenProps={{}}
                />
            )}
            <LoadingModal style={{ display: loadScreenToggle }}><LoaderSpinner /></LoadingModal>
            <IfDisconnect status={!iOnline} action={checkOnline} />
            {showVersionPrompt && (
                <AppVersionPrompt
                    remoteVersion={versionCheck.remoteVersion}
                    currentVersion={__APP_VERSION__}
                    onUpdate={versionCheck.update}
                    onSkip={handleVersionSkip}
                />
            )}
            {isPatchOpen && canViewPatchNote && (
                <AppModal
                    title='מה חדש'
                    controlFunc={() => setIsPatchOpen(false)}
                    Children={PatchNoteView}
                    childrenProps={{}}
                    size='md'
                />
            )}
            <Layout>
                <Sidebar
                    mode={isMobile ? 'mobile-drawer' : 'desktop'}
                    collapsed={!isMobile && collapsed}
                    onToggleCollapse={toggleCollapsed}
                    drawerOpen={drawerOpen}
                    onCloseDrawer={closeDrawer}
                    modules={modules}
                    pathname={location.pathname}
                    showTestLink={getLogin === 'טסט1'}
                    showSettings={isAllowToShowEdits}
                    user={{
                        name: logoName?.name || '',
                        subRole: subName,
                        companyName: logoName?.company_name || '',
                    }}
                    appVersion={__APP_VERSION__}
                    hasNewVersion={hasNewVersion}
                    onOpenPatchNote={canViewPatchNote ? () => setIsPatchOpen(true) : undefined}
                    onOpenVersionPrompt={() => setForceShowPrompt(true)}
                />
                {isMobile && !drawerOpen && (
                    <BurgerBtn type='button' aria-label='תפריט' onClick={openDrawer}>
                        <svg
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        >
                            <path d='M4 6h16M4 12h16M4 18h16' />
                        </svg>
                    </BurgerBtn>
                )}
                <Content>
                    <BrodMigdalProvider>
                        <ContextCefi.Provider value={[cefiNum, setCefiNum]}>
                            <ErrorBoundary FallbackComponent={GeneralFallback}>
                                <AppRouting />
                            </ErrorBoundary>
                        </ContextCefi.Provider>
                    </BrodMigdalProvider>
                </Content>
            </Layout>
        </MainSection>
    ) : (
        <LoadingModal>
            <LoaderSpinner />
        </LoadingModal>
    )
}

export default SecondScreen
